/* eslint-disable no-restricted-globals */

/**
 * Clean our shorthand value prior to passing in the payload as a string to be used in a url later.
 * @see https://stackoverflow.com/questions/9635625/javascript-regex-to-remove-illegal-characters-from-a-url
 */
const slugify = (str: string): string => {
  str = str.trim().toLowerCase();

  // remove accents, swap ñ for n, etc
  const from = 'àáäâèéëêìíïîòóöôùúüûñç·/_,:;';
  const to = 'aaaaeeeeiiiioooouuuunc------';
  for (let i = 0, l = from.length; i < l; i++) {
    str = str.replace(new RegExp(from.charAt(i), 'g'), to.charAt(i));
  }

  str = str
    .replace(/[^a-z0-9 -]/g, '') // remove invalid chars
    .replace(/\s+/g, '-') // collapse whitespace and replace by -
    .replace(/-+/g, '-'); // collapse dashes

  return str;
};

const includeLeadingSlash = (path: string | undefined) => {
  if (!path) return '/';
  if (!path.startsWith('/')) {
    return `/${path}`;
  }
  return path;
};

/**
 * Returns the base path of the application, *without* a trailing slash.
 * This base path is used to prefix all relative URLs in the application.
 *
 * The base path is expected to be set via `window.BC_BASE_PATH` (in the browser)
 * or `self.BC_BASE_PATH` (in web workers). If not defined, it returns an empty string.
 *
 * Example return values:
 *   - ""        → when deployed at https://example.com/
 *   - "/my-app" → when deployed at https://example.com/my-app/
 *
 * NOTE:
 * This function returns a relative path or an empty string. It is *not*
 * guaranteed to be valid as an absolute URL (e.g. for fetch() in jsdom or SDKs
 * like Faro). Callers that require a full absolute URL should use a wrapper
 * that resolves this appropriately.
 */
const getBasePath = (): string => {
  let basePath = '';
  if (typeof window !== 'undefined') {
    basePath = window.BC_BASE_PATH;
  }
  if (!basePath && typeof self !== 'undefined') {
    basePath = self.BC_BASE_PATH;
  }
  return (basePath ?? '').replace(/\/$/, '');
};

const includeBasePath = (path: string): string => {
  const base = getBasePath();
  if (!base) return includeLeadingSlash(path);
  if (!path) return includeLeadingSlash(base);
  if (path === base || path.startsWith(`${base}/`)) return includeLeadingSlash(path);
  const joinedPath = [base, includeLeadingSlash(path)].join('');
  return includeLeadingSlash(joinedPath);
};

export { getBasePath, includeBasePath, slugify };
